import type { VersionInfo } from '@adornis/baseql/protocol.js';
import { html } from 'lit';
import bundledVersion from '../../../../version.json' with { type: 'json' };

/**
 * What `parseVersion` returns when it cannot read version.json. Treated as "the server did not tell
 * us", never as a mismatch — otherwise a missing file in the image would prompt every user to reload
 * on every connection, and reloading would not help.
 */
const UNKNOWN_RELEASE = 'unknown';

const versionKey = (version: VersionInfo) => `${version.release}|${version.commit}`;

const isUsable = (version: VersionInfo | undefined): version is VersionInfo =>
  !!version?.release && version.release !== UNKNOWN_RELEASE;

/**
 * Whether the tab is running an older bundle than the server it just connected to, and we have not
 * already asked about that exact server version.
 *
 * Split out from the dialog so the decision is testable on its own — the guards matter more than the
 * comparison, see version-check.test.ts.
 */
export function shouldPromptReload(
  serverVersion: VersionInfo | undefined,
  bundled: VersionInfo,
  alreadyPromptedKey?: string,
) {
  if (!isUsable(serverVersion) || !isUsable(bundled)) return false;

  const serverKey = versionKey(serverVersion);
  return serverKey !== versionKey(bundled) && serverKey !== alreadyPromptedKey;
}

/** server version we already prompted for, so a reconnect does not re-open the dialog */
let promptedKey: string | undefined;

/**
 * Compares the version the server announces on every connection ack against the one baked into this
 * bundle and offers a reload when they differ. Wired into the transport in `startup.ts`.
 *
 * A tab left open across a deploy keeps running its old bundle, which is how clients end up calling
 * operations the server no longer registers.
 */
export async function promptReloadOnVersionMismatch(serverVersion: VersionInfo) {
  if (!shouldPromptReload(serverVersion, bundledVersion, promptedKey)) return;

  // keyed on the server version, so a later deploy asks again
  promptedKey = versionKey(serverVersion);

  // loaded here rather than at module scope: the dialog pulls in config that demands ROOT_URL, which
  // would make shouldPromptReload impossible to import on its own
  const { confirmDialog } = await import('@adornis/dialog/dialog-functions.js');

  const reload = await confirmDialog(
    html`
      <div class="flex flex-col gap-2">
        <p class="font-semibold">Neue Version verfügbar</p>
        <p>
          Diese Seite läuft noch mit einer älteren Version des Portals (${bundledVersion.release}). Bitte laden Sie sie
          neu, sonst können einzelne Aktionen fehlschlagen.
        </p>
      </div>
    `,
    { acceptText: 'Jetzt neu laden', declineText: 'Später' },
  );

  if (reload) location.reload();
}
